import { Fragment, forwardRef, useEffect, useState } from "react";
import { flip, offset, useClick, useFloating, useInteractions } from "@floating-ui/react";
import type { Editor } from "@tiptap/core";
import { Menu } from "@headlessui/react";

import ChevronDownIcon from "@assets/icons/chevron-down.svg";

import { DEFAULT_ACTIONS } from "./constants";
import type { Divider as ActionDivider, Action } from "./types";
import { cn } from "@utils/tailwind.util";
import { Tooltip } from "../../ui/Tooltip/Tooltip";
import { Divider } from "../../ui/Divider";
import { Button } from "../../ui/Button";

export type IToolbarProps = {
  editor: Editor | null;
  actions?: (Action | ActionDivider)[];
  className?: string;
};

type IToolbarButton = {
  action: Action;
  editor: Editor | null;
  active?: boolean;
  iconOnly?: boolean;
  tooltip?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
};

type IToolbarMenuButton = {
  action: Action;
  editor: Editor | null;
};

function isAction(action: Action | ActionDivider): action is Action {
  return "label" in action && "icon" in action;
}

export const ToolbarButton = forwardRef<HTMLButtonElement, IToolbarButton>(
  ({ action, editor, active = false, iconOnly = true, tooltip = true, children, onClick }, ref) => {
    const isActionActive = editor && action.isActive?.(editor);

    const button = (
      <Button
        buttonType="TRANSPARENT"
        className={cn(
          "w-full justify-start px-1 py-2",
          "dark:hover:bg-gray-600",
          {
            "h-7": iconOnly,
            "hover:!bg-gray-100 dark:hover:!bg-gray-600": !iconOnly,
            "bg-gray-100 dark:bg-gray-600": !iconOnly && active,
            "bg-action-secondaryPressed dark:bg-gray-500": !iconOnly && isActionActive,
            "bg-action-secondaryPressed dark:bg-gray-500 shadow-scoutButtonPressed":
              iconOnly && isActionActive,
          },
          "focus:enabled:bg-action-secondaryPressed dark:focus:enabled:bg-gray-600"
        )}
        ref={ref}
        isDisabled={editor ? action.disabled?.(editor) : false}
        onClick={() => {
          if (!editor) return;
          action.onClick?.(editor);
          onClick?.();
        }}
      >
        {children ?? (
          <>
            <action.icon className="inline h-5 w-5 flex-none align-middle text-content-secondary dark:text-invertedPrimary80" />
            {!iconOnly && (
              <span className="ml-1 inline align-middle text-sm font-medium text-content-primary dark:text-invertedPrimary80">
                {action.label}
              </span>
            )}
          </>
        )}
      </Button>
    );

    return (
      <div>
        {tooltip ? (
          <Tooltip
            placement="top"
            tooltipContent={action.label}
            variant="dark"
            root={document.querySelector("[data-headlessui-portal]") as HTMLElement}
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </div>
    );
  }
);

export const ToolbarMenuButton = forwardRef<HTMLButtonElement, IToolbarMenuButton>(
  ({ action, editor }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      placement: "top-start",
      middleware: [flip(), offset(8)],
    });

    const click = useClick(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([click]);

    return (
      <Menu key={action.label} as="div" className="relative">
        <div>
          <Menu.Button
            as="div"
            className="flex items-center"
            ref={refs.setReference}
            {...getReferenceProps()}
          >
            {({ open }) => (
              <ToolbarButton ref={ref} active={open} action={action} editor={editor}>
                <action.icon className="inline h-5 w-5 flex-none align-middle text-content-secondary dark:text-invertedPrimary80" />
                <ChevronDownIcon
                  width={12}
                  height={12}
                  viewBox="0 0 20 20"
                  className="relative -ml-1 inline align-middle text-content-secondary dark:text-invertedPrimary80"
                />
              </ToolbarButton>
            )}
          </Menu.Button>
        </div>

        <Menu.Items
          className="w-56 rounded-md bg-background-primary dark:bg-gray-850 p-1 shadow-md ring-1 ring-stroke-primary dark:ring-gray-600 focus:outline-none z-10"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {action.children!.map((child, index) => {
            if ("divider" in child) {
              return (
                <Menu.Item
                  as={Divider}
                  className="my-1 dark:border-gray-600"
                  key={index}
                  disabled
                />
              );
            }

            return (
              <Menu.Item key={action.label} as={Fragment}>
                {({ active, close }) => (
                  <ToolbarButton
                    active={active}
                    action={child}
                    editor={editor}
                    iconOnly={false}
                    tooltip={false}
                    onClick={close}
                  />
                )}
              </Menu.Item>
            );
          })}
        </Menu.Items>
      </Menu>
    );
  }
);

export const ToolbarDivider = () => {
  return <div className="inline h-7 w-[1px] bg-stroke-primary dark:bg-gray-600 align-middle" />;
};

export const Toolbar = ({ actions = DEFAULT_ACTIONS, className, editor }: IToolbarProps) => {
  const [_, forceUpdate] = useState(0);

  useEffect(() => {
    if (!editor) return;

    editor.on("transaction", () => {
      forceUpdate((prev) => prev + 1);
    });

    return () => {
      editor.off("transaction");
    };
  }, [editor]);

  return (
    <div className={cn("flex justify-center items-center py-3 px-[21px]", className)}>
      <div className="flex justify-between gap-1">
        {actions.map((action, index) => {
          if (isAction(action) && action.children?.length) {
            return <ToolbarMenuButton key={index} action={action} editor={editor} />;
          }

          if ("divider" in action) {
            return <ToolbarDivider key={`level-1-${index}`} />;
          }

          return (
            <ToolbarButton
              key={`level-1-${action.label}`}
              action={action}
              editor={editor}
              tooltip={action.tooltip}
            />
          );
        })}
      </div>
    </div>
  );
};
